/**
 * Behavioural analytics — what is associated with a conversion.
 *
 * Four questions, each with its own honesty problem, all surfaced rather than
 * smoothed over:
 *
 *  - Which referrers convert better? Real differences exist, but they are
 *    associations. Every row shows its sample so the reader can discount a
 *    38.7% built on 279 information items.
 *  - How long after a marketing event does the admission land? Median 0 days,
 *    because the source writes a lead record at admission. The tail is the
 *    finding, not the middle.
 *  - How soon does a referrer send the next patient after a discharge? Median
 *    45 days — and the referrers whose own cadence has doubled are the list
 *    worth acting on.
 *  - B2B vs B2C? Not in the source. Shown as what it actually is.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { EChartsOption } from "echarts";
import { AlertTriangle, Info } from "lucide-react";

import { api } from "@/api/client";
import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { useFilterStore } from "@/store/filters";
import type { AnalyticsEnvelope, Metadata, Quality } from "@/types/api";
import { count, inr } from "@/utils/format";

interface CohortRow {
  label: string;
  clients: number;
  information: number;
  admissions: number;
  revenue: number;
  conversion_pct: number | null;
  vs_overall_points: number | null;
  suppressed: boolean;
  revenue_per_admission: number | null;
  activities?: number;
}
interface Block {
  dimension: string; label: string; note: string; rows: CohortRow[];
}
interface Bucket { bucket: string; count: number; pct: number }
interface Slowing {
  client_id: string; name: string; usual_gap_days: number;
  latest_gap_days: number; times_slower: number; referrals: number;
}

const TABS = [
  { key: "patterns", label: "Conversion patterns" },
  { key: "channels", label: "Referrer channels" },
  { key: "cadence", label: "Referral cadence" },
  { key: "event-lag", label: "Event to admission" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function useBehaviour<S>(path: string, enabled: boolean) {
  const params = useFilterStore((s) => s.queryParams());
  return useQuery({
    queryKey: ["behaviour", path, params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<S>>(
        `/analytics/behaviour/${path}`, { params });
      return data;
    },
    enabled,
    staleTime: 600_000,
  });
}

/** Bars for a lag distribution. One series, so no legend — the title names it. */
function histogramOption(buckets: Bucket[], colour: string): EChartsOption {
  return {
    tooltip: {
      trigger: "axis",
      backgroundColor: "#16211F",
      borderWidth: 0,
      textStyle: { color: "#F6F8F7", fontSize: 12 },
      formatter: (raw: unknown) => {
        const rows = raw as Array<{ dataIndex: number }>;
        const b = buckets[rows[0]?.dataIndex ?? 0];
        return b ? `<strong>${b.bucket}</strong><br/>${count(b.count)} · ${b.pct}%` : "";
      },
    },
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    xAxis: { type: "category", data: buckets.map((b) => b.bucket),
             axisLabel: { rotate: 35, fontSize: 10 } },
    yAxis: { type: "value" },
    series: [{
      type: "bar",
      data: buckets.map((b) => b.count),
      // 4px rounded data-end anchored to the baseline.
      itemStyle: { color: colour, borderRadius: [4, 4, 0, 0] },
      barMaxWidth: 34,
    }],
  };
}

/** A cohort table. The bar encodes distance from the population rate, which is
 *  the only comparison that means anything — 24% is good or bad only relative
 *  to what the whole business does. */
function CohortTable({ block, overall }: { block: Block; overall: number | null }) {
  const widest = Math.max(1, ...block.rows.map(
    (r) => Math.abs(r.vs_overall_points ?? 0)));
  return (
    <section className="card p-4">
      <h3 className="font-display font-semibold text-[15px]">{block.label}</h3>
      {block.note && (
        <p className="text-[12px] text-ink-muted mt-0.5 mb-2">{block.note}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr>
              {["Cohort", "Conversion", "vs all", "Information", "Admissions", "Revenue"]
                .map((h, i) => (
                  <th key={h} className={`label border-b border-rule px-2 py-2 ${
                    i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((r) => {
              const delta = r.vs_overall_points;
              const good = (delta ?? 0) > 0;
              return (
                <tr key={r.label} className="border-b border-rule last:border-0">
                  <td className="px-2 py-2">{r.label}</td>
                  <td className="px-2 py-2 text-right font-mono tnum">
                    {r.suppressed ? (
                      <span className="text-ink-faint" title={
                        `Withheld: ${count(r.information)} information items is below the floor`}>
                        —
                      </span>
                    ) : `${r.conversion_pct}%`}
                  </td>
                  <td className="px-2 py-2">
                    {delta === null ? (
                      <span className="text-ink-faint text-[11.5px]">too few</span>
                    ) : (
                      <span className="flex items-center gap-2 justify-end">
                        <span className="font-mono tnum text-[12px]"
                              style={{ color: good ? "#2F6B3C" : "#9E3B2E" }}>
                          {delta > 0 ? "+" : ""}{delta}pt
                        </span>
                        <span className="w-16 h-1.5 rounded-full bg-surface-alt overflow-hidden shrink-0">
                          <span className="block h-full rounded-full"
                                style={{
                                  width: `${(Math.abs(delta) / widest) * 100}%`,
                                  background: good ? "#2F6B3C" : "#9E3B2E",
                                }} />
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tnum">
                    {count(r.information)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tnum">
                    {count(r.admissions)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tnum">
                    {inr(r.revenue)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {overall !== null && (
        <p className="text-[11.5px] text-ink-faint mt-2">
          Population conversion is {overall}%. "vs all" is the gap in
          percentage points.
        </p>
      )}
    </section>
  );
}

function Caveats({ quality }: { quality?: Quality }) {
  if (!quality?.notes?.length) return null;
  return (
    <div className="card p-4 space-y-1.5">
      <div className="label flex items-center gap-1.5">
        <Info size={12} /> how to read this
      </div>
      {quality.notes.map((note) => (
        <p key={note} className="text-[12.5px] text-ink-soft">{note}</p>
      ))}
    </div>
  );
}

export function BehaviourPage() {
  const [tab, setTab] = useState<TabKey>("patterns");

  const patterns = useBehaviour<{
    overall_conversion_pct: number | null; information: number;
    admissions: number; clients: number; min_cohort: number;
  }>("patterns", tab === "patterns");
  const channels = useBehaviour<{
    overall_conversion_pct: number | null; information: number;
    admissions: number; clients: number;
  }>("channels", tab === "channels");
  const cadence = useBehaviour<{
    n: number; p25: number | null; median: number | null;
    p75: number | null; p90: number | null;
    clients_with_cadence: number; slowing_count: number;
  }>("cadence", tab === "cadence");
  const lag = useBehaviour<{
    links: number; exact_links: number; inferred_links: number;
    all: { n: number; median: number | null; p75: number | null; p90: number | null };
    exact_only: { median: number | null; p90: number | null };
  }>("event-lag", tab === "event-lag");

  const active = { patterns, channels, cadence, "event-lag": lag }[tab];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display font-bold text-[22px] sm:text-[24px] tracking-tight">
          Behavioural analytics
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          What is associated with a conversion, and how long things take. These
          are differences between cohorts and distributions of delay — not
          predictions, and not causes.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1 bg-surface-alt rounded-md p-0.5 w-fit">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 rounded text-[12.5px] ${
                    tab === t.key ? "bg-surface shadow-card font-medium text-ink"
                                  : "text-ink-muted hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {active?.isLoading && <div className="skeleton h-80" />}

      {/* ---- conversion patterns ---- */}
      {tab === "patterns" && !patterns.isLoading && patterns.data && (
        <>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
            <KpiCard label="Population conversion"
                     value={patterns.data.summary.overall_conversion_pct === null
                       ? "—" : `${patterns.data.summary.overall_conversion_pct}%`}
                     footnote="admissions ÷ information received" />
            <KpiCard label="Information received"
                     value={count(patterns.data.summary.information)} />
            <KpiCard label="Admissions"
                     value={count(patterns.data.summary.admissions)} />
            <KpiCard label="Clients compared"
                     value={count(patterns.data.summary.clients)}
                     footnote={`cohorts under ${patterns.data.summary.min_cohort} items withheld`} />
          </div>
          {(patterns.data.breakdown as unknown as Block[]).map((block) => (
            <CohortTable key={block.dimension} block={block}
                         overall={patterns.data!.summary.overall_conversion_pct} />
          ))}
          <Caveats quality={patterns.data.quality} />
          <Disclosure quality={patterns.data.quality}
                      metadata={patterns.data.metadata as Metadata} />
        </>
      )}

      {/* ---- channels ---- */}
      {tab === "channels" && !channels.isLoading && channels.data && (
        <>
          <div className="card p-4 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
            <p className="text-[13px] text-ink-soft">
              <strong>This is not a B2B/B2C split.</strong> The source has no
              such field — <code className="font-mono text-[12px]">institution</code>{" "}
              is empty on all 10,881 clients. What it does record is
              Medico/Non-Medico, and a designation whose largest value
              ("Others", 57% of clients) is left unclassified rather than
              guessed at.
            </p>
          </div>
          {(channels.data.breakdown as unknown as Block[]).map((block) => (
            <CohortTable key={block.dimension} block={block}
                         overall={channels.data!.summary.overall_conversion_pct} />
          ))}
          <Caveats quality={channels.data.quality} />
        </>
      )}

      {/* ---- cadence ---- */}
      {tab === "cadence" && !cadence.isLoading && cadence.data && (
        <>
          <div className="card p-4 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
            <p className="text-[13px] text-ink-soft">
              "How soon is the referral given after discharge" is not answerable
              as asked: the referral credit row is raised around billing, so on
              83% of leads it is dated <em>before</em> the discharge (median
              −3 days). What is measured here is the referrer's own cadence —
              how long after one patient is discharged the next referral arrives.
            </p>
          </div>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))" }}>
            <KpiCard label="Median gap"
                     value={`${cadence.data.summary.median ?? "—"} days`} />
            <KpiCard label="Quartile" value={`${cadence.data.summary.p25 ?? "—"} days`}
                     footnote="fastest 25% arrive within" />
            <KpiCard label="P90" value={`${cadence.data.summary.p90 ?? "—"} days`}
                     footnote="the slow tail" />
            <KpiCard label="Referrers slowing"
                     value={count(cadence.data.summary.slowing_count)}
                     higherIsBetter={false}
                     footnote="latest gap over 2× their own usual" />
          </div>
          <section className="card p-4">
            <h3 className="font-display font-semibold text-[15px]">
              Discharge to next referral
            </h3>
            <p className="text-[12px] text-ink-muted mb-2">
              {count(cadence.data.summary.n)} gaps across{" "}
              {count(cadence.data.summary.clients_with_cadence)} referrers.
            </p>
            <Chart height={260}
                   option={histogramOption(
                     cadence.data.breakdown as unknown as Bucket[], "#2563C9")} />
          </section>
          <section className="card p-4">
            <h3 className="font-display font-semibold text-[15px]">
              Referrers whose cadence has slowed
            </h3>
            <p className="text-[12px] text-ink-muted mb-2">
              Compared against their own history, not against each other — a
              weekly referrer and a quarterly one are not comparable.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr>
                    {["Referrer", "Usual gap", "Latest gap", "Slower by", "Referrals"]
                      .map((h, i) => (
                        <th key={h} className={`label border-b border-rule px-2 py-2 ${
                          i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {(cadence.data.trend as unknown as Slowing[]).map((r) => (
                    <tr key={r.client_id} className="border-b border-rule last:border-0">
                      <td className="px-2 py-2">{r.name}</td>
                      <td className="px-2 py-2 text-right font-mono tnum">
                        {r.usual_gap_days}d
                      </td>
                      <td className="px-2 py-2 text-right font-mono tnum">
                        {r.latest_gap_days}d
                      </td>
                      <td className="px-2 py-2 text-right font-mono tnum text-warn">
                        {r.times_slower}×
                      </td>
                      <td className="px-2 py-2 text-right font-mono tnum">
                        {r.referrals}
                      </td>
                    </tr>
                  ))}
                  {(cadence.data.trend as unknown as Slowing[]).length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-8 text-center text-ink-muted">
                      No referrer has slowed by more than 2× their usual gap.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          <Caveats quality={cadence.data.quality} />
        </>
      )}

      {/* ---- event to admission ---- */}
      {tab === "event-lag" && !lag.isLoading && lag.data && (
        <>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))" }}>
            <KpiCard label="Median" value={`${lag.data.summary.all.median ?? "—"} days`}
                     footnote="lead records are written at admission" />
            <KpiCard label="P90" value={`${lag.data.summary.all.p90 ?? "—"} days`} />
            <KpiCard label="Linked activities"
                     value={count(lag.data.summary.links)} />
            <KpiCard label="Keyed, not inferred"
                     value={count(lag.data.summary.exact_links)}
                     footnote="deterministic task_ref link" />
          </div>
          <section className="card p-4">
            <h3 className="font-display font-semibold text-[15px]">
              Days from marketing activity to admission
            </h3>
            <p className="text-[12px] text-ink-muted mb-2">
              All links. A median of 0 is expected — the source creates the lead
              record at admission, so same-day is the norm and the tail carries
              the signal.
            </p>
            <Chart height={260}
                   option={histogramOption(
                     lag.data.breakdown as unknown as Bucket[], "#C2670A")} />
          </section>
          <section className="card p-4">
            <h3 className="font-display font-semibold text-[15px]">
              Keyed links only
            </h3>
            <p className="text-[12px] text-ink-muted mb-2">
              Excludes windowed matches, which cannot tell cause from
              coincidence.
            </p>
            <Chart height={260}
                   option={histogramOption(
                     lag.data.trend as unknown as Bucket[], "#0B7A5E")} />
          </section>
          <Caveats quality={lag.data.quality} />
        </>
      )}
    </div>
  );
}
